import type { DashboardKind } from "@/api/dashboards";

/**
 * The address of one `dashboards` row (plan 2026-10-26, D1): a report lives
 * under Reports, a dashboard under Insights → Dashboards. Tenant-relative —
 * the account-routing `navigate`/`Link` add the `/a/:accountPublicId` prefix.
 *
 * Use it wherever the row (and so its kind) is at hand. A site that knows only
 * an id keeps the dashboard path: the page redirects a report to `/reports/…`
 * once its row has loaded.
 */
export function dashboardPath(row: { id: string; kind?: DashboardKind | null }, search = ""): string {
  return `${dashboardListPath(row.kind)}/${encodeURIComponent(row.id)}${search}`;
}

/** The list a row of this kind belongs to: the back link, and where a delete lands. */
export function dashboardListPath(kind: DashboardKind | null | undefined): string {
  return kind === "report" ? "/reports" : "/insights/dashboards";
}

/**
 * Where the open page belongs, or null when it is already there. Run only on
 * a row loaded for the URL's id, so a stale row (mid-switch between two
 * reports) can neither loop nor send the viewer to the previous report.
 */
export function kindRedirectPath(
  pathname: string,
  urlId: string,
  kind: DashboardKind | null | undefined,
  search: string,
): string | null {
  const onReports = pathname.startsWith("/reports/");
  const isReport = kind === "report";
  if (onReports === isReport) return null;
  return dashboardPath({ id: urlId, kind: isReport ? "report" : "dashboard" }, search);
}
